/**
 * SSE (Server-Sent Events) 客户端
 * 替代 WebSocket 方案，用于接收服务端实时通知推送
 *
 * 优势：
 * - 浏览器原生自动重连（无需手写重连逻辑）
 * - 标准HTTP协议，调试友好（浏览器 Network 面板可见）
 * - 无需心跳管理（服务端发送 SSE 注释帧保持连接）
 *
 * 消息类型：
 *   NOTIFICATION — 业务通知（审批/账单/工单等）
 *   MENU_UPDATE  — 菜单变更广播
 */

import { useUserStore } from '@/stores/user'
import { usePermissionStore } from '@/stores/permission'
import { ElNotification } from 'element-plus'
import router from '@/router'
import { useDashboardStore } from '@/stores/dashboard'
import request from '@/utils/request'

/**
 * 获取 SSE 基础 URL
 * 开发环境通过 Vite 代理，生产环境直连后端
 */
function getSseBaseUrl() {
  if (import.meta.env.VITE_WS_BASE_URL) {
    // 复用 WS_BASE_URL 配置，将 ws:// 转为 http://
    return import.meta.env.VITE_WS_BASE_URL
      .replace(/^wss:/, 'https:')
      .replace(/^ws:/, 'http:')
      .replace(/\/api\/?$/, '')
  }
  // 默认使用当前页面的 origin（走 Vite 代理或 Nginx）
  return ''
}

const SSE_BASE_URL = getSseBaseUrl()

/**
 * SSE 客户端类
 */
export class SseClient {
  constructor() {
    /** @type {EventSource|null} */
    this.source = null
    /** @type {boolean} 是否手动关闭 */
    this.manualClose = false
    /** @type {boolean} 是否已连接 */
    this.isConnected = false
  }

  /**
   * 建立 SSE 连接
   * <p>
   * 安全流程：先用 Authorization Header 调 POST /sse/ticket 拿一次性 ticket，
   * 再用 ticket 建立 EventSource 连接。长期 JWT 不会出现在 URL 中。
   * </p>
   */
  async connect() {
    if (this.source && this.source.readyState === EventSource.OPEN) {
      console.debug('[SSE] 已连接，跳过重复连接')
      return
    }

    const userStore = useUserStore()
    const token = userStore.token

    if (!token) {
      console.warn('[SSE] 无Token，无法建立连接')
      return
    }

    this.manualClose = false

    // 1. 用 Authorization Header 换取一次性 ticket
    let ticket
    try {
      const resp = await request({ url: '/sse/ticket', method: 'post' })
      ticket = resp?.data?.ticket
      if (!ticket) {
        console.error('[SSE] 获取ticket失败: 响应中无ticket字段', resp)
        return
      }
    } catch (e) {
      console.error('[SSE] 获取ticket失败:', e)
      return
    }

    try {
      // 2. 用一次性 ticket 建立 SSE 连接（长期 JWT 不进 URL）
      const url = `${SSE_BASE_URL}/api/sse/subscribe?ticket=${encodeURIComponent(ticket)}`
      console.debug('[SSE] 尝试连接:', url)

      this.source = new EventSource(url)

      this.source.onopen = () => {
        console.debug('[SSE] 连接成功')
        this.isConnected = true
      }

      this.source.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          this.handleMessage(data)
        } catch (e) {
          console.error('[SSE] 消息解析失败:', e)
        }
      }

      this.source.onerror = (event) => {
        console.debug('[SSE] 连接错误或断开')
        this.isConnected = false

        // 一次性 ticket 模式下，浏览器原生自动重连会复用已消费的 ticket 必失败，
        // 必须手动关闭旧连接，重新走 ticket 流程建立新连接。
        const currentSource = this.source
        if (currentSource) {
          try { currentSource.close() } catch (_) { /* ignore */ }
        }
        this.source = null

        if (!this.manualClose) {
          // 退避后再连接，避免后端短暂故障时疯狂请求 ticket
          setTimeout(() => {
            if (!this.manualClose) {
              this.connect()
            }
          }, 3000)
        }
      }

    } catch (error) {
      console.error('[SSE] 创建连接失败:', error)
      this.isConnected = false
    }
  }

  /**
   * 处理接收到的消息
   *
   * @param {Object} data - 消息数据 { type, data, timestamp }
   */
  handleMessage(data) {
    console.debug('[SSE] 收到消息:', data)

    switch (data.type) {
      case 'NOTIFICATION':
        this.handleNotification(data.data || {})
        break

      case 'MENU_UPDATE':
        this.handleMenuUpdate(data.data || {})
        break

      default:
        console.debug('[SSE] 未知消息类型:', data.type)
    }
  }

  /**
   * 处理通知推送消息
   */
  handleNotification(payload) {
    console.debug('[SSE] 收到通知推送:', payload)

    // 显示桌面通知
    ElNotification({
      title: payload.title || '新消息',
      message: payload.content || '',
      type: payload.priority === 'HIGH' ? 'warning' : 'info',
      duration: 5000,
      onClick: () => {
        if (payload.link) {
          router.push(payload.link)
        }
      }
    })

    // 更新 dashboard store 未读数
    try {
      const dashboardStore = useDashboardStore()
      dashboardStore.handleNotificationPush()
    } catch (e) {
      // store 可能未初始化，忽略
    }
  }

  /**
   * 处理菜单更新消息
   */
  async handleMenuUpdate(data) {
    const { menuId, operation } = data

    console.debug('[SSE] 菜单更新:', { menuId, operation })

    // 延迟500ms后重新加载路由（给服务端一点时间完成数据库操作）
    await new Promise(resolve => setTimeout(resolve, 500))

    try {
      const permissionStore = usePermissionStore()
      await permissionStore.reloadRoutes(router)

      const operationMessages = {
        'CREATE': '新增成功，菜单已同步',
        'UPDATE': '更新成功，菜单已同步',
        'DELETE': '删除成功，菜单已同步'
      }

      ElNotification({
        title: '菜单更新',
        message: operationMessages[operation] || '菜单已变更，已同步',
        type: 'success',
        duration: 2000
      })
    } catch (error) {
      console.error('[SSE] 重载路由失败:', error)
      ElNotification({
        title: '同步失败',
        message: '菜单已更新但同步失败，请刷新页面',
        type: 'warning',
        duration: 5000
      })
    }
  }

  /**
   * 断开连接
   */
  disconnect() {
    console.debug('[SSE] 手动断开连接')
    this.manualClose = true
    this.isConnected = false

    if (this.source) {
      this.source.close()
      this.source = null
    }
  }

  /**
   * 获取连接状态
   *
   * @returns {boolean} 是否已连接
   */
  getConnectionStatus() {
    return this.isConnected && this.source && this.source.readyState === EventSource.OPEN
  }
}

/**
 * 全局 SSE 客户端实例
 */
export const sseClient = new SseClient()

export default sseClient
